import React, { useRef, useState } from "react";
import { ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import { finApi, finErr } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, Loading, useLoad, when } from "@/components/nds/windows/finance/parts";
import { TxTable } from "@/components/nds/windows/finance/OutsideIncomeTab";

/** Supporting document: upload to object storage, open via authenticated link. */
export function DocAttach({ prefix, rec, onChanged, testid, locked, client = finApi }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { await client.upload(`${prefix}/${rec.id}/document`, file); toast.success("Document attached"); onChanged(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-white/5">
      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Document</span>
      {rec.document_url ? (
        <button type="button" onClick={() => client.openFile(rec.document_url).catch((e) => toast.error(finErr(e)))} data-testid={`${testid}-doc-link`} className="inline-flex items-center gap-1.5 text-sm text-nex-gold hover:underline">
          {rec.document_name || "Open document"} <ExternalLink className="h-3 w-3" />
        </button>
      ) : <span data-testid={`${testid}-doc-none`} className="text-sm text-nex-muted">None attached</span>}
      {rec.status !== "voided" && !locked && (
        <>
          <input ref={ref} type="file" accept=".pdf,image/*" onChange={upload} className="hidden" data-testid={`${testid}-doc-input`} />
          <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-doc-upload`} className={`${btnGold} inline-flex items-center gap-1.5`}>
            <Upload className="h-3 w-3" /> {busy ? "Uploading…" : rec.document_url ? "Replace" : "Upload"}
          </button>
        </>
      )}
    </div>
  );
}

/** Status-transition audit log stored on the record. */
export function HistoryList({ history, chips, testid }) {
  if (!history?.length) return <Empty testid={`${testid}-empty`}>No history yet.</Empty>;
  return (
    <ol data-testid={testid} className="p-5 space-y-2">
      {[...history].reverse().map((h, i) => (
        <li key={i} data-testid={`${testid}-item-${i}`} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-mono text-[10px] text-nex-muted w-40">{when(h.at)}</span>
          {h.from_status && <Chip tone={(chips[h.from_status] || ["grey"])[0]}>{(chips[h.from_status] || [, h.from_status])[1]}</Chip>}
          {h.from_status && <span className="text-nex-muted">→</span>}
          <Chip tone={(chips[h.to_status] || ["grey"])[0]}>{(chips[h.to_status] || [, h.to_status])[1]}</Chip>
          <span className="text-nex-silver/80">{h.actor?.name}</span>
          {h.note && <span className="text-nex-muted">· {h.note}</span>}
        </li>
      ))}
    </ol>
  );
}

/** fin_transactions rows that reference a record. */
export function RefLedger({ refType, refId, testid, deps = [] }) {
  const [state, load] = useLoad(() => finApi.get("/transactions", { reference_type: refType, reference_id: refId, page_size: 500 }), [refId, ...deps]);
  if (state.loading && !state.data) return <Loading testid={`${testid}-loading`} />;
  if (state.error) return <ErrorBox testid={`${testid}-error`} error={state.error} onRetry={load} />;
  return (
    <div className="p-5 space-y-3">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Ledger audit trail · {state.data.total} rows</div>
      {state.data.items.length ? <TxTable testid={testid} items={state.data.items} /> : <Empty testid={`${testid}-empty`}>No ledger activity yet.</Empty>}
    </div>
  );
}

/** Inline note/amount input + confirm, used for pay / void / waive / edit actions. */
export function InlineAction({ id, mode, numeric, placeholder, confirmClass, onConfirm, onCancel, busy , optional }) {
  const [val, setVal] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <input autoFocus type={numeric ? "number" : "text"} min="0.01" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder}
        data-testid={`${id}-${mode}-input`} className={`[color-scheme:dark] bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm px-3 py-1.5 rounded-sm ${numeric ? "w-44" : "flex-1 min-w-[200px]"}`} />
      <button type="button" disabled={busy || (!optional && !String(val).trim())} onClick={() => onConfirm(val)} data-testid={`${id}-${mode}-confirm`} className={confirmClass}>Confirm {mode}</button>
      <button type="button" onClick={onCancel} data-testid={`${id}-${mode}-cancel`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
    </div>
  );
}
